/**
 * Estado observable del runtime STT (backend, modelos warm, NPU probe).
 */

/** @typedef {'wasm'|'webgpu'|'webnn-npu'} SttBackend */
/** @typedef {'idle'|'loading'|'ready'|'inferring'} SttRuntimeStatus */
/** @typedef {'cache'|'network'|'memory'|null} SttLoadSource */

const BACKEND_LABELS = {
  wasm: 'CPU/WASM',
  webgpu: 'WebGPU',
  'webnn-npu': 'NPU',
}

/** @type {object} */
let state = {
  backend: 'wasm',
  backendLabel: 'CPU/WASM',
  modelsWarm: [],
  lastLoadSource: null,
  npuAvailable: false,
  npuActive: false,
  status: 'idle',
}

/** @type {Set<Function>} */
const listeners = new Set()

let npuProbeStarted = false

/**
 * @returns {object}
 */
export function getSttRuntimeSnapshot() {
  return { ...state, modelsWarm: state.modelsWarm.slice() }
}

/**
 * @param {Partial<typeof state>} patch
 */
export function updateSttRuntime(patch) {
  if (!patch || typeof patch !== 'object') return
  const next = { ...state, ...patch }
  if (patch.modelsWarm) {
    next.modelsWarm = patch.modelsWarm.slice()
  }
  if (patch.backend && BACKEND_LABELS[patch.backend]) {
    next.backendLabel = BACKEND_LABELS[patch.backend]
    next.npuActive = patch.backend === 'webnn-npu'
  }
  state = next
  listeners.forEach((fn) => {
    try {
      fn(getSttRuntimeSnapshot())
    } catch (e) {
      /* ignore */
    }
  })
}

/**
 * @param {(snap: object) => void} cb
 * @returns {() => void}
 */
export function subscribeSttRuntime(cb) {
  if (typeof cb !== 'function') return () => {}
  listeners.add(cb)
  try {
    cb(getSttRuntimeSnapshot())
  } catch (e) {
    /* ignore */
  }
  return () => listeners.delete(cb)
}

/**
 * Probe pasivo WebNN-NPU (no activa backend).
 * @returns {Promise<boolean>}
 */
export async function probeWebnnNpuAvailable() {
  if (typeof navigator === 'undefined' || !navigator.ml) return false
  try {
    const ctx = await navigator.ml.createContext({ deviceType: 'npu' })
    return !!ctx
  } catch (e) {
    return false
  }
}

/**
 * Inicia probe NPU una vez (idle).
 */
export function scheduleNpuProbe() {
  if (npuProbeStarted) return
  npuProbeStarted = true
  const run = () => {
    void probeWebnnNpuAvailable().then((ok) => {
      updateSttRuntime({ npuAvailable: !!ok })
    })
  }
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(run, { timeout: 4000 })
  } else {
    setTimeout(run, 500)
  }
}

/**
 * @param {object} [msg] worker message
 */
export function applyWorkerProgressToRuntime(msg) {
  if (!msg) return
  const patch = { status: 'loading' }
  if (msg.fromCache || msg.phase === 'ready') {
    patch.lastLoadSource = msg.phase === 'ready' ? 'memory' : 'cache'
  } else if (msg.phase === 'download' || msg.status === 'download') {
    patch.lastLoadSource = 'network'
  }
  if (msg.backend) {
    patch.backend = msg.backend
  }
  if (Array.isArray(msg.modelsLoaded)) {
    patch.modelsWarm = msg.modelsLoaded
    if (msg.modelsLoaded.length > 0) {
      patch.status = 'ready'
    }
  }
  updateSttRuntime(patch)
}

/**
 * @param {object} [msg]
 */
export function applyWorkerReadyToRuntime(msg) {
  const patch = {
    status: 'ready',
    lastLoadSource: msg && msg.cached ? 'memory' : 'cache',
  }
  if (msg && msg.backend) patch.backend = msg.backend
  if (msg && Array.isArray(msg.modelsLoaded)) {
    patch.modelsWarm = msg.modelsLoaded
  }
  updateSttRuntime(patch)
}

export function markSttInferring() {
  updateSttRuntime({ status: 'inferring' })
}

export function markSttIdle() {
  updateSttRuntime({
    status: state.modelsWarm.length > 0 ? 'ready' : 'idle',
  })
}

export default {
  getSttRuntimeSnapshot,
  updateSttRuntime,
  subscribeSttRuntime,
  probeWebnnNpuAvailable,
  scheduleNpuProbe,
  applyWorkerProgressToRuntime,
  applyWorkerReadyToRuntime,
  markSttInferring,
  markSttIdle,
}
